import { useState, useEffect, useCallback } from "react";
import { Wifi, WifiOff } from "lucide-react";

import { readToken } from '../../auth/session';

const POLL_INTERVAL = 10000; // 10 seconds

/**
 * Displays the local agent connection status in the navbar.
 * Polls GET /api/agent/status every 10 seconds.
 */
const AgentStatusIndicator = ({ onClick }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkStatus = useCallback(async () => {
    try {
      const token = readToken();
      if (!token) return;

      const response = await fetch("/api/agent/status", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setConnected(data.connected);
      }
    } catch (err) {
      // Silently fail — agent status is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkStatus]);

  if (loading) return null;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 h-control-sm px-3 rounded-md text-xs font-medium transition-all cursor-pointer ${
        connected
          ? "bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 text-green-700 dark:text-green-300"
          : "bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 text-orange-700 dark:text-orange-300"
      }`}
      title={
        connected
          ? "Local Agent Connected — localhost APIs route through your machine"
          : "Click to connect Local Agent — required for testing localhost APIs"
      }
      aria-label={connected ? "Local agent connected" : "Connect local agent for localhost API testing"}
    >
      {connected ? (
        <>
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <Wifi className="w-3.5 h-3.5 text-green-500" />
          <span className="hidden sm:inline text-green-700 dark:text-green-300">Agent Connected</span>
        </>
      ) : (
        <>
          <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
          <WifiOff className="w-3.5 h-3.5 text-orange-400" />
          <span className="hidden sm:inline text-orange-700 dark:text-orange-300">Connect Agent</span>
        </>
      )}
    </button>
  );
};

export default AgentStatusIndicator;
